/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {
  SWIPER_OPTIONS as BASE_SWIPER_OPTIONS,
  SWIPER_MODULE_DEFAULTS,
} from '@amblique/base/app/constants/swiper-options';
import breakpoints from '@amblique/king-living/app/theme/foundations/breakpoints';
import {
  ACCOUNT_LANDING_SUGGESTED_CAROUSEL_GAP_DESKTOP,
  ACCOUNT_LANDING_SUGGESTED_CAROUSEL_GAP_MOBILE,
  ACCOUNT_LANDING_SUGGESTED_CAROUSEL_SLIDES_DESKTOP,
  ACCOUNT_LANDING_SUGGESTED_CAROUSEL_SLIDES_MOBILE,
} from '@amblique/king-living/app/constants/account';

/**
 * CF-016: `loop` + `freeMode` + `slidesPerView: 'auto'` — re-measure / `loopFix` / `slideToLoop(0)` on a
 * staggered schedule so mixed slides (e.g. ShopTheLook after fetch) don’t leave translate stuck at 0.
 * @param {import('swiper').Swiper} swiper
 */
function cf016ScheduleLoopAnchorAfterInit(swiper) {
  if (!swiper || swiper.destroyed || !swiper.params?.loop || typeof window === 'undefined') return

  const lightAnchor = () => {
    if (!swiper || swiper.destroyed) return
    try {
      swiper.updateSize()
      swiper.updateSlides()
      swiper.update()
      swiper.loopFix?.()
      swiper.slideToLoop?.(0, 0)
      swiper.updateProgress?.()
      swiper.updateSlidesClasses?.()
    } catch {
      /* loop / freeMode races */
    }
  }

  const heavyAnchor = () => {
    if (!swiper || swiper.destroyed) return
    try {
      swiper.updateSize()
      swiper.updateSlides()
      swiper.loopDestroy?.()
      swiper.loopCreate?.()
      swiper.update()
      swiper.loopFix?.()
      swiper.slideToLoop?.(0, 0)
      swiper.updateProgress?.()
      swiper.updateSlidesClasses?.()
    } catch {
      /* same */
    }
  }

  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => {
      lightAnchor()
      requestAnimationFrame(lightAnchor)
    })
  } else {
    lightAnchor()
  }

  const lightMs = [0, 80, 200, 450, 900, 1600, 2400, 3800]
  const heavyMs = [550, 2000, 5000, 10000]

  const timeoutIds = [
    ...lightMs.map((ms) => window.setTimeout(lightAnchor, ms)),
    ...heavyMs.map((ms) => window.setTimeout(heavyAnchor, ms)),
  ]

  const onDestroy = () => {
    timeoutIds.forEach((id) => window.clearTimeout(id))
    if (typeof swiper.off === 'function') {
      swiper.off('destroy', onDestroy)
    }
  }

  if (typeof swiper.on === 'function') {
    swiper.on('destroy', onDestroy)
  }
}

/**
 * CF-046 only: after nav, Swiper often prepends and leaves nothing after active+1 (blank right
 * peek). Append next buffer via loopFix({ direction: 'next' }). No setTranslate/slideTo —
 * those re-enter slideChange and freeze the page. Locked so it cannot recurse.
 *
 * @param {import('swiper').Swiper} swiper
 */
function cf046AppendLoopBuffer(swiper) {
  if (!swiper || swiper.destroyed || !swiper.params?.loop) return
  if (swiper.__klCf046LoopFixLock) return

  swiper.__klCf046LoopFixLock = true
  try {
    swiper.loopFix?.({direction: 'next'})
  } catch {
    /* ignore */
  }
  setTimeout(() => {
    if (!swiper.destroyed) swiper.__klCf046LoopFixLock = false
  }, 0)
}

/**
 * CF-046 + CMS `loop: false`: `watchOverflow` sets `isLocked` only when `snapGrid.length === 1`.
 * With desktop `slidesPerView: 'auto'` + few wide slides (e.g. 2 × 647px), Swiper still builds
 * 2 snaps so arrows never get `.swiper-button-lock`. Force-lock that case; keep `slidesPerGroup: 1`.
 *
 * @param {import('swiper').Swiper} swiper
 */
function cf046SyncNavLockWhenLoopOff(swiper) {
  if (!swiper || swiper.destroyed || swiper.params?.loop) return

  swiper.checkOverflow?.()

  const slideCount = swiper.slides?.length ?? 0
  const isAutoSpv = swiper.params?.slidesPerView === 'auto'
  // Desktop auto + ≤2 CMS slides: design wants arrows hidden; native watchOverflow won't.
  const forceLock = isAutoSpv && slideCount > 0 && slideCount <= 2

  if (!forceLock) {
    swiper.navigation?.update?.()
    return
  }

  if (!swiper.isLocked) {
    swiper.isLocked = true
    if (swiper.params.allowSlideNext === true) swiper.allowSlideNext = false
    if (swiper.params.allowSlidePrev === true) swiper.allowSlidePrev = false
    swiper.emit?.('lock')
  }

  swiper.navigation?.update?.()
}

/**
 * Default `.swiper-button-*` matches every nav in the document — nested instock tiles + outer list
 * would all receive the same instance's click handlers. Scope `nextEl` / `prevEl` per carousel.
 * @see swiper/modules/navigation.mjs `getEl` + `uniqueNavElements`
 */
const WANT_IT_SOONER_NAVIGATION = {
  ...SWIPER_MODULE_DEFAULTS.navigation,
  nextEl: '.swiper-want-it-sooner > .swiper-navigation-pagination .swiper-button-next',
  prevEl: '.swiper-want-it-sooner > .swiper-navigation-pagination .swiper-button-prev',
};

const INSTOCK_TILE_NAVIGATION = {
  ...SWIPER_MODULE_DEFAULTS.navigation,
  nextEl: '.swiper-instock-tile > .swiper-navigation-pagination .swiper-button-next',
  prevEl: '.swiper-instock-tile > .swiper-navigation-pagination .swiper-button-prev',
};

/**
 * Product Tiles carousel (outer list): scope nav to a **stable** root class so nested in-tile nav
 * never matches. Preset adds `swiper-product-tiles-carousel` or `swiper-product-tiles-carousel-3` — the
 * outer `<Swiper>` also sets `swiper-product-tiles-carousel` in `product-tiles.jsx` so this selector
 * always matches (3-up only had `-3`, which broke `nextEl` / `prevEl` resolution).
 */
const PRODUCT_TILES_CAROUSEL_NAVIGATION = {
  ...SWIPER_MODULE_DEFAULTS.navigation,
  nextEl: '.swiper-product-tiles-carousel > .swiper-navigation-pagination .swiper-button-next',
  prevEl: '.swiper-product-tiles-carousel > .swiper-navigation-pagination .swiper-button-prev',
};

/**
 * Range PLP tile (KINDW-99 / FSD PLP-03): scope nav inside `.swiper-navigation-pagination` so each
 * tile's prev/next stays isolated when many tiles are mounted in the grid.
 */
const RANGE_TILE_NAVIGATION = {
  ...SWIPER_MODULE_DEFAULTS.navigation,
  nextEl: '.swiper-range-tile > .swiper-navigation-pagination .swiper-button-next',
  prevEl: '.swiper-range-tile > .swiper-navigation-pagination .swiper-button-prev',
};

/** Inner tile swiper nested in an outer carousel slide — Swiper root class differs from `range_tile`. */
const RANGE_TILE_CAROUSEL_SLIDE_NAVIGATION = {
  ...SWIPER_MODULE_DEFAULTS.navigation,
  nextEl: '.swiper-range-tile-carousel-slide > .swiper-navigation-pagination .swiper-button-next',
  prevEl: '.swiper-range-tile-carousel-slide > .swiper-navigation-pagination .swiper-button-prev',
};

/** Scoped nav selectors for in-tile product hero swipers (Range + Instock). */
export const PRODUCT_TILE_IMAGE_NAVIGATION = RANGE_TILE_NAVIGATION;
export const PRODUCT_TILE_CAROUSEL_SLIDE_IMAGE_NAVIGATION = RANGE_TILE_CAROUSEL_SLIDE_NAVIGATION;

const RANGE_TILE_PAGINATION = {
  clickable: false,
  enabled: true,
  type: 'progressbar',
};

/**
 * In-tile product hero swiper (RangeProductTile, Contentful tiles, cart upsell, etc.).
 * Navigation is OFF by default — `ProductTileImageGallery` enables it for desktop 3+ only.
 * Progress bar on all breakpoints; no dot pagination.
 * `loop` (not `rewind`) so touch swipe wraps at first/last slide on mobile; `rewind` only
 * applies to nav buttons / slideNext(), not finger swipe.
 * Want It Sooner uses legacy `instock_tile` preset instead (unchanged behaviour).
 */
const PRODUCT_TILE_IMAGE_SWIPER = {
  effect: 'slide',
  autoHeight: false,
  loop: true,
  rewind: false,
  slidesPerGroup: 1,
  slidesPerView: 1,
  autoplay: false,
  keyboard: {enabled: true},
  a11y: {enabled: true},
  preventClicksPropagation: true,
  pagination: RANGE_TILE_PAGINATION,
  scrollbar: false,
  allowTouchMove: true,
  navigation: false,
  responsive: {
    mobile: {
      allowTouchMove: true,
      touchStartPreventDefault: false,
    },
    tablet: {
      allowTouchMove: true,
    },
    desktop: {
      allowTouchMove: true,
    },
  },
};

/**
 * Swiper options presets
 * @link [Swiper API](https://swiperjs.com/swiper-api#parameters)
 */
export const SWIPER_OPTIONS = {
  ...BASE_SWIPER_OPTIONS,


  /**
   * Default options - always applied
   */
  'default': {
    ...BASE_SWIPER_OPTIONS.default,

    pagination: true,
    slidesPerView: 1,
    slidesPerGroup: 1,
  },


  /**
   * Product Gallery
   * - Rewind wraps last→first / first→last without DOM cloning
   * - Progressbar pagination (progress line below gallery)
   * - Arrows hidden on mobile, visible on tablet/desktop
   * - No auto-rotate
   */
  'product_gallery': {
    effect: 'slide',
    rewind: true,
    slidesPerGroup: 1,
    slidesPerView: 1,
    autoplay: false,
    loop: true,
    keyboard: {enabled: true},
    pagination: {
      enabled: true,
      type: 'progressbar',
    },
    touchStartPreventDefault: false,
    responsive: {
      mobile: {
        autoHeight: false,
        navigation: false,
        touchAngle: 30,
        touchStartPreventDefault: false,
        threshold: 12,
      },
      tablet: {navigation: true},
      desktop: {navigation: true},
    },
  },

  /**
   * Product Gallery Complex (PDP for configurable products)
   * - Rewind wraps last→first / first→last without DOM cloning
   * - Progressbar pagination (progress line below gallery)
   * - Arrows hidden on mobile, visible on tablet/desktop
   * - No auto-rotate
   */
  'product_gallery_complex': {
    effect: 'slide',
    rewind: true,
    slidesPerGroup: 1,
    slidesPerView: 1,
    autoplay: false,
    keyboard: {enabled: true},
    loop: true,
    pagination: {
      enabled: true,
      type: 'progressbar',
    },
    touchStartPreventDefault: false,
    responsive: {
      mobile: {
        autoHeight: false,
        navigation: false,
        touchAngle: 30,
        touchStartPreventDefault: false,
        threshold: 12,
      },
      tablet: {navigation: true},
      desktop: {navigation: true},
    },
  },

  /**
   * Want it sooner.
   */
  'want_it_sooner': {
    navigation: false,
    pagination: false,
    noSwiping: false,
    slidesPerGroup: 1,
    slidesPerView: 'auto',
    spaceBetween: 3,
    touchStartPreventDefault: false,
    autoHeight: false,
    responsive: {
      mobile: {navigation: false, touchStartPreventDefault: false},
      tablet: {navigation: WANT_IT_SOONER_NAVIGATION},
      desktop: {navigation: WANT_IT_SOONER_NAVIGATION},
    },
    breakpoints: {
      [parseInt(breakpoints.breakpoints.md)]: {
        slidesPerGroup: 4,
        slidesPerView: 4,
        spaceBetween: 3,
      },
    },
  },

  /**
   * **Inside each product tile hero** — swipe + progress bar; arrows only when enabled per tile (3+ desktop).
   * `loop` keeps mobile touch swipe cycling; do not use `rewind` here (rewind ignores touch at edges).
   */
  'range_tile': PRODUCT_TILE_IMAGE_SWIPER,

  /** @deprecated Alias — use `range_tile`. Kept for backwards compatibility. */
  'range_tile_blog': PRODUCT_TILE_IMAGE_SWIPER,

  /**
   * **Contentful Card `textAboveSwatch` mobile** — touch swipe only; dots render in info stack
   */
  'contentful_card_mobile': {
    effect: 'slide',
    autoHeight: false,
    rewind: true,
    slidesPerGroup: 1,
    slidesPerView: 1,
    autoplay: false,
    keyboard: {enabled: true},
    a11y: {enabled: true},
    preventClicksPropagation: true,
    pagination: false,
    navigation: false,
    scrollbar: false,
    allowTouchMove: true,
    touchStartPreventDefault: false,
  },

  /**
   * **Inside each InstockProductTile** — image-only swiper (chevrons + progress bar).
   * **Want It Sooner / InstockProductTile only** — legacy in-tile swiper (chevrons + progress bar).
   * Not part of unified `ProductTileImageGallery` behaviour.
   */
  'instock_tile': {
    effect: 'slide',
    autoHeight: false,
    allowTouchMove: false,
    preventClicksPropagation: true,
    rewind: true,
    slidesPerGroup: 1,
    slidesPerView: 1,
    autoplay: false,
    navigation: INSTOCK_TILE_NAVIGATION,
    pagination: {
      clickable: false,
      enabled: true,
      type: 'progressbar',
    },
  },

  /**
   * **Inside each product tile nested in an outer carousel slide** (account landing, CF product tiles).
   * Matches `instock_tile`: chevrons + progress bar, no touch — outer carousel keeps horizontal swipe.
   */
  'range_tile_carousel_slide': {
    effect: 'slide',
    autoHeight: false,
    allowTouchMove: false,
    preventClicksPropagation: true,
    rewind: true,
    slidesPerGroup: 1,
    slidesPerView: 1,
    autoplay: false,
    navigation: RANGE_TILE_CAROUSEL_SLIDE_NAVIGATION,
    pagination: RANGE_TILE_PAGINATION,
  },

  /**
   * **Product Tiles outer carousel — 4-up desktop** (CF-049.1 `appearance: Carousel`).
   * Preset matches `resolveProductTilesDesktopColumnCount`: `'4.2'`, unset, or anything other than `'3'`/`'3.2'`.
   * Slide visual width at lg: **317px** (see theme variant `product-tiles-carousel`, `product-tiles-layout.js`).
   * Scoped nav selectors prevent collision with nested `instock_tile` image swipers.
   * - Mobile/tablet: **`slidesPerView: 'auto'`** so slide width follows CSS (`PRODUCT_TILE_CAROUSEL_MOBILE_SLIDE_WIDTH_PX`). Numeric `slidesPerView` (e.g. `1.2`) would inject inline widths and ignore theme.
   * - Desktop (lg+): 4 slides per view, nav arrows enabled.
   */
  'product-tiles-carousel': {
    autoHeight: false,
    allowTouchMove: true,
    slidesPerGroup: 1,
    slidesPerView: 'auto',
    autoplay: false,
    pagination: false,
    watchOverflow: true,
    responsive: {
      mobile: {navigation: false, allowTouchMove: true, spaceBetween: 4},
      tablet: {navigation: false, allowTouchMove: true},
      desktop: {navigation: PRODUCT_TILES_CAROUSEL_NAVIGATION, allowTouchMove: true, spaceBetween: 3},
    },
    breakpoints: {
      [parseInt(breakpoints.breakpoints.lg)]: {
        slidesPerView: 4,
        slidesPerGroup: 1,
      },
    },
  },

  /**
   * **Product Tiles outer carousel — 3-up desktop** (`numberItemsPerView` `'3'` or `'3.2'`).
   * Slide visual width at lg: **424px** (theme `product-tiles-carousel-3`).
   * Below lg: same as `product-tiles-carousel` — `slidesPerView: 'auto'` + CSS mobile width.
   */
  'product-tiles-carousel-3': {
    autoHeight: false,
    allowTouchMove: true,
    slidesPerGroup: 1,
    slidesPerView: 'auto',
    autoplay: false,
    pagination: false,
    watchOverflow: true,
    responsive: {
      mobile: {navigation: false, allowTouchMove: true, spaceBetween: 4},
      tablet: {navigation: false, allowTouchMove: true},
      desktop: {navigation: PRODUCT_TILES_CAROUSEL_NAVIGATION, allowTouchMove: true, spaceBetween: 3},
    },
    breakpoints: {
      [parseInt(breakpoints.breakpoints.lg)]: {
        slidesPerView: 3,
        slidesPerGroup: 1,
      },
    },
  },

  'CF-010': {
    // KL-1103: Media Carousel main — infinite loop (CMS can set options.loop: false).
    loop: true,
    autoplay: {
      enabled: true,
      delay: 5000,
      pauseOnMouseEnter: false,
    },
    navigation: true,
    pagination: {
      enabled: true,
      type: 'fraction',
    },
  },

  'CF-011': {
    loop: true,
    navigation: false,
    pagination: false,
  },

  'CF-011-Thumbs': {
    // Thumbs strip — never loop (KL-1103).
    loop: false,
    slidesPerView: 4,
    slidesPerGroup: 4,
    spaceBetween: 40,
    navigation: false,
    pagination: false,
    keyboard: false,
    breakpoints: {
      [parseInt(breakpoints.breakpoints.base)]: {
        slidesPerView: 1,
        slidesPerGroup: 1,
        spaceBetween: 16,
      },
      [parseInt(breakpoints.breakpoints.md)]: {
        slidesPerView: 3,
        slidesPerGroup: 3,
        spaceBetween: 40,
      },
    },
  },

  'CF-012': {
    // KL-1103: Media Carousel main — infinite loop (CMS can set options.loop: false).
    loop: true,
    navigation: true,
    pagination: false,
  },

  'CF-012-Thumbs': {
    loop: false,
    slidesPerView: 'auto',
    spaceBetween: 20,
    navigation: false,
    pagination: false,
    allowTouchMove: true,
    centerInsufficientSlides: true,
    centerSlides: true,
    centeredSlidesBounds: true,
    breakpoints: {
      [parseInt(breakpoints.breakpoints.lg)]: {
        allowTouchMove: false,
      },
    },
  },

  'CF-013': {
    // KL-1103: Media Carousel main — infinite loop (CMS can set options.loop: false).
    loop: true,
    navigation: false,
    pagination: false,
    slidesPerGroup: 1,
    slidesPerView: 1,
  },

  'CF-013-Thumbs': {
    loop: false,
    navigation: false,
    pagination: false,
    autoHeight: false,
    keyboard: false,
    slidesPerView: 'auto',
    centerInsufficientSlides: true,
    centerSlides: true,
    centeredSlidesBounds: true,
    breakpoints: {
      [parseInt(breakpoints.breakpoints.base)]: {
        spaceBetween: 26,
      },
      [parseInt(breakpoints.breakpoints.md)]: {
        spaceBetween: 56,
      },
    },
  },

  /**
   * Media carousel (mixed slides). Plain `Image` slides get stable widths quickly (`img` + clamp in theme).
   * `ShopTheLook` often: skeleton while `useShopTheLookById` runs → then `ShopTheLookBlock` flex layout;
   * `slidesPerView: 'auto'` + `loop` measure slides too early, so loop/freeMode looks broken until layout
   * settles (a manual swipe forces `update`). If `watchOverflow` locks before real widths exist, wrapper
   * stays at translate 0. A single late `slideToLoop(0)` is flaky (STL may finish before or after 2s).
   * Use staggered anchors: `update`+`loopFix`+`slideToLoop(0)`, plus sparse full rebuilds.
   */
  'CF-016': {
    navigation: false,
    pagination: false,
    loop: true,
    loopAddBlankSlides: true,
    loopPreventsSliding: false,
    slidesPerView: 'auto',
    roundLengths: true,
    watchOverflow: false,
    slidesPerGroup: 1,
    watchSlidesProgress: true,
    autoHeight: false,
    spaceBetween: 4,
    freeMode: true,
    observer: true,
    observeParents: true,
    resizeObserver: true,
    onAfterInit: cf016ScheduleLoopAnchorAfterInit,
  },

  'CF-022': {
    // KL-1103: Media Carousel main — infinite loop (CMS can set options.loop: false).
    loop: true,
    navigation: false,
    pagination: false,
    slidesPerView: 1.5,
    spaceBetween: 20,
    watchOverflow: true,
    observer: true,
    observeParents: true,
    resizeObserver: true,
    loopAdditionalSlides: 2,
    loopAddBlankSlides: true,
    loopPreventsSliding: false,
    freeMode: {
      enabled: true,
      momentumRatio: 0.4,
      momentumVelocityRatio: 0.4,
    },
    breakpoints: {
      [parseInt(breakpoints.breakpoints.md)]: {
        slidesPerView: 'auto',
      },
    },
  },

  /**
   * CF-057 — same card rail as CF-022, with bottom-right circular prev/next (scroll buttons).
   * CMS: Media Carousel Appearance = CF-057. Pair with Split Heading like CF-022.
   */
  'CF-057': {
    loop: true,
    navigation: true,
    pagination: false,
    slidesPerView: 1.5,
    spaceBetween: 20,
    watchOverflow: true,
    observer: true,
    observeParents: true,
    resizeObserver: true,
    loopAdditionalSlides: 2,
    loopAddBlankSlides: true,
    loopPreventsSliding: false,
    freeMode: {
      enabled: true,
      momentumRatio: 0.4,
      momentumVelocityRatio: 0.4,
    },
    breakpoints: {
      [parseInt(breakpoints.breakpoints.md)]: {
        slidesPerView: 'auto',
      },
    },
  },

  /**
   * Media Carousel (showroom): mobile 1.2 slides + 2px gap, no arrows; desktop auto widths + arrows.
   * KINDW-842: freeMode off, autoHeight off (merged default breaks layout), watchSlidesProgress for nav state.
   * KL-1103: `loop: true` on main; re-measure + loopFix after init (slidesPerView auto needs it).
   */
  'CF-046': {
    // KL-1103: loop + slidesPerView auto + few CMS slides → blank right peek after nav.
    // MediaCarousel pads CF-046 slides (e.g. 3 → 9). Here: extra buffer, no blank fillers,
    // append next-side after each transition (cf046AppendLoopBuffer).
    loop: true,
    loopAdditionalSlides: 3,
    loopAddBlankSlides: false,
    loopPreventsSliding: false,
    navigation: true,
    scrollbar: false,
    pagination: false,
    freeMode: false,
    slidesPerGroup: 1,
    slidesPerView: 1.2,
    spaceBetween: 2,
    autoHeight: false,
    initialSlide: 0,
    watchSlidesProgress: true,
    roundLengths: true,
    watchOverflow: true,
    observer: true,
    observeParents: true,
    breakpoints: {
      [parseInt(breakpoints.breakpoints.md)]: {
        slidesPerView: 'auto',
        spaceBetween: 4,
      },
    },
    onAfterInit(swiper) {
      const u = () => {
        if (!swiper || swiper.destroyed) return;
        swiper.update();
        if (swiper.params?.loop) {
          // CMS loop on (default): keep next-side buffer filled.
          swiper.loopFix?.();
          cf046AppendLoopBuffer(swiper);
        } else {
          // CMS options.loop: false — native checkOverflow + force-lock for ≤2 auto slides.
          cf046SyncNavLockWhenLoopOff(swiper);
        }
      };
      u();
      requestAnimationFrame(() => {
        u();
        requestAnimationFrame(u);
      });
      setTimeout(u, 0);
      // Late image/auto width pass (slidesPerView: 'auto').
      setTimeout(u, 300);
    },
    onSlideChangeTransitionEnd(swiper) {
      if (!swiper || swiper.destroyed) return;
      if (swiper.params?.loop) {
        cf046AppendLoopBuffer(swiper);
      } else {
        swiper.update();
        cf046SyncNavLockWhenLoopOff(swiper);
      }
    },
    onResize(swiper) {
      if (!swiper || swiper.destroyed || swiper.params?.loop) return;
      cf046SyncNavLockWhenLoopOff(swiper);
    },
    // Swiper may re-run checkOverflow after observer/update and clear isLocked — re-apply.
    onUpdate(swiper) {
      if (!swiper || swiper.destroyed || swiper.params?.loop) return;
      cf046SyncNavLockWhenLoopOff(swiper);
    },
  },

  /**
   * CF-050 — Blog related articles: mobile ~1 slide + peek, desktop 3 per view; no loop.
   * Navigation scoped in `blog-related-articles.jsx`; arrows lg+ only (theme).
   */
  'CF-050': {
    loop: false,
    pagination: false,
    freeMode: false,
    autoHeight: false,
    slidesPerGroup: 1,
    slidesPerView: 'auto',
    spaceBetween: 20,
    watchOverflow: true,
    allowTouchMove: true,
    touchStartPreventDefault: false,
    breakpoints: {
      [parseInt(breakpoints.breakpoints.lg)]: {
        slidesPerView: 3,
        spaceBetween: 40,
        slidesPerGroup: 1,
      },
    },
  },

  /**
   * ACCOUNT-02 — Suggested for you on My Account landing (Figma 5759:50664 / 5759:56823).
   * Mobile ~1.265 peek → ~280px slide @ 375px viewport; gap 3px; desktop 3-up · gap 4px.
   */
  'account-landing-product-tiles': {
    autoHeight: false,
    allowTouchMove: true,
    slidesPerGroup: 1,
    slidesPerView: ACCOUNT_LANDING_SUGGESTED_CAROUSEL_SLIDES_MOBILE,
    spaceBetween: ACCOUNT_LANDING_SUGGESTED_CAROUSEL_GAP_MOBILE,
    autoplay: false,
    pagination: false,
    watchOverflow: true,
    responsive: {
      mobile: {navigation: false, allowTouchMove: true, spaceBetween: ACCOUNT_LANDING_SUGGESTED_CAROUSEL_GAP_MOBILE},
      tablet: {navigation: false, allowTouchMove: true, spaceBetween: ACCOUNT_LANDING_SUGGESTED_CAROUSEL_GAP_MOBILE},
      desktop: {navigation: PRODUCT_TILES_CAROUSEL_NAVIGATION, allowTouchMove: true, spaceBetween: ACCOUNT_LANDING_SUGGESTED_CAROUSEL_GAP_DESKTOP},
    },
    breakpoints: {
      [parseInt(breakpoints.breakpoints.lg)]: {
        slidesPerView: ACCOUNT_LANDING_SUGGESTED_CAROUSEL_SLIDES_DESKTOP,
        slidesPerGroup: 1,
        spaceBetween: ACCOUNT_LANDING_SUGGESTED_CAROUSEL_GAP_DESKTOP,
      },
    },
  },

  /**
   * ACCOUNT-02 — Get inspired on My Account landing: same carousel rules as suggested-for-you.
   */
  'account-landing-get-inspired': {
    loop: false,
    pagination: false,
    freeMode: false,
    autoHeight: false,
    slidesPerGroup: 1,
    slidesPerView: 1.1993,
    spaceBetween: 20,
    watchOverflow: true,
    allowTouchMove: true,
    touchStartPreventDefault: false,
    breakpoints: {
      [parseInt(breakpoints.breakpoints.lg)]: {
        slidesPerView: 3,
        spaceBetween: 40,
        slidesPerGroup: 1,
      },
    },
  },

  /**
   * Configuration thumbnail swiper (View details section)
   * Used in ConfigurationGalleryBlock for c_pimlyConfigurationImage thumbnails
   * Mobile: 1.5 slides visible. Desktop: 1 slide (230px), arrows stick out.
   * `watchOverflow: true` adds `.swiper-button-lock` when nav is hidden — Simple PDP
   * uses that class in `product-page-global.js` to inset the thumbnail column.
   */
  'configuration-thumbnail': {
    navigation: true,
    pagination: {clickable: true},
    scrollbar: false,
    watchOverflow: true,
    spaceBetween: 12,
    slidesPerView: 1.5,
    slidesPerGroup: 1,
    breakpoints: {
      [parseInt(breakpoints.breakpoints.base)]: {
        slidesPerView: 1.5,
        slidesPerGroup: 1,
      },
      [parseInt(breakpoints.breakpoints.lg)]: {
        slidesPerView: 1,
        slidesPerGroup: 1,
      },
    },
  },

  /**
   * Account Navigation - Mobile horizontal swiper
   * Non-continuous carousel (stops at ends)
   */
  'account-nav': {
    navigation: true,
    pagination: false,
    slidesPerView: 'auto',
    spaceBetween: '24px',
    freeMode: true,
    watchSlidesProgress: true,
    centeredSlides: false,
    centeredSlidesBounds: false,
    rewind: false,
  },

  /**
   * Cover swiper (Choose your cover step)
   * Slide width auto, no arrows on mobile, arrows on desktop
   */
  'cover-swiper': {
    slidesPerView: 3.5,
    spaceBetween: 8,
    navigation: false,
    pagination: false,
    autoHeight: false,
    freeMode: true,
    responsive: {
      mobile: {navigation: false},
      tablet: {navigation: false},
      desktop: {navigation: true},
    },
  },
};

export * from '@amblique/base/app/constants/swiper-options';
